import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import {
  Search, ShieldCheck, Code, AlertTriangle, Wrench, Layout, GraduationCap, Briefcase,
  CheckCircle2, ArrowRight, ArrowUpRight
} from 'lucide-react';

function ServicesPage() {
  const detailedServices = [
    {
      title: 'Diagnóstico inicial y escaneo de vulnerabilidades',
      description: 'Evaluación rápida y precisa de tu superficie de ataque. Identificamos puertos abiertos, servicios expuestos y vulnerabilidades conocidas en tu infraestructura pública.',
      icon: Search,
      benefits: ['Visibilidad inmediata de riesgos', 'Reporte ejecutivo claro', 'Priorización de parches'],
    },
    {
      title: 'Auditoría de seguridad integral',
      description: 'Revisión profunda de tus sistemas, redes y procesos internos. Evaluamos el cumplimiento de normativas y las mejores prácticas de la industria.',
      icon: ShieldCheck,
      benefits: ['Análisis de configuraciones', 'Revisión de políticas de acceso', 'Alineación con estándares (ISO, CIS)'],
    },
    {
      title: 'Pruebas de penetración (pentesting)',
      description: 'Simulamos ataques cibernéticos reales de forma controlada para descubrir cómo un atacante podría comprometer tus sistemas y datos.',
      icon: Code,
      benefits: ['Validación real de defensas', 'Identificación de brechas lógicas', 'Prueba de concepto de explotación'],
    },
    {
      title: 'Respuesta a incidentes',
      description: 'Asistencia experta e inmediata en caso de una brecha de seguridad, ataque de ransomware o compromiso de datos para minimizar el impacto.',
      icon: AlertTriangle,
      benefits: ['Contención rápida de amenazas', 'Análisis forense digital', 'Recuperación segura de operaciones'],
    },
    {
      title: 'Implementación de herramientas y soluciones',
      description: 'Selección, instalación y configuración de tecnologías de seguridad (Firewalls, EDR, SIEM, WAF) adaptadas a tu presupuesto y necesidades.',
      icon: Wrench,
      benefits: ['Despliegue sin interrupciones', 'Configuración óptima', 'Transferencia de conocimiento'],
    },
    {
      title: 'Arquitectura y consultoría de seguridad',
      description: 'Diseño de redes seguras y estrategias de protección a largo plazo. Te ayudamos a construir seguridad desde el diseño (Security by Design).',
      icon: Layout,
      benefits: ['Diseño Zero Trust', 'Revisión de arquitectura Cloud', 'Estrategia de seguridad escalable'],
    },
    {
      title: 'Concientización de usuarios y políticas',
      description: 'Programas de entrenamiento para empleados y desarrollo de políticas de seguridad claras para reducir el riesgo de errores humanos.',
      icon: GraduationCap,
      benefits: ['Simulaciones de Phishing', 'Creación de manuales de uso', 'Reducción de riesgo interno'],
    },
    {
      title: 'Gestión de riesgos y vCISO',
      description: 'Servicio de Director de Seguridad de la Información virtual. Liderazgo estratégico continuo sin el costo de un ejecutivo a tiempo completo.',
      icon: Briefcase,
      benefits: ['Dirección estratégica', 'Gestión de proveedores', 'Reportes a la junta directiva'],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Servicios de Ciberseguridad | Faronexo</title>
        <meta name="description" content="Conocé en detalle nuestros servicios de consultoría en ciberseguridad para pymes: diagnóstico, auditoría, pentesting, respuesta a incidentes y más." />
      </Helmet>

      <Header />

      <main className="bg-background">
        {/* Page hero */}
        <section className="fx-hero" style={{ paddingBottom: '8px' }}>
          <div className="fx-container">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="fx-hero-copy"
              style={{ paddingBlock: '30px' }}
            >
              <span className="fx-eyebrow"><span className="fx-dot" /> Catálogo de soluciones</span>
              <h1>Servicios de <span>seguridad</span> para pymes</h1>
              <p className="fx-intro" style={{ maxWidth: '620px' }}>
                Soluciones minimalistas, efectivas y adaptadas a la realidad de tu empresa. Protegemos lo que importa sin complejidades innecesarias.
              </p>
              <div className="fx-actions">
                <a href="#evaluacion" className="fx-button">
                  Solicitar evaluación rápida
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/solicitar-evaluacion" className="fx-text-link text-muted-foreground">
                  Solicitar un servicio
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Detailed services */}
        <section className="fx-section" style={{ paddingTop: '40px' }}>
          <div className="fx-container">
            <div className="space-y-5">
              {detailedServices.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.article
                    key={index}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45 }}
                    className="group grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-start p-7 md:p-8 rounded-xl border border-white/10 bg-[#152032] hover:border-[#ffbf5080] transition-colors"
                  >
                    <div className="w-14 h-14 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:bg-[#ffbf5020] transition-colors">
                      <Icon className="w-7 h-7" style={{ color: '#ffbf50' }} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <h2 className="text-xl md:text-2xl font-semibold text-foreground group-hover:text-[#ffbf50] transition-colors" style={{ letterSpacing: '-.025em' }}>
                          {service.title}
                        </h2>
                        <span className="text-sm text-[#9ba8bb] font-mono shrink-0">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <p className="text-[#aeb9c9] text-base md:text-lg leading-relaxed mb-5" style={{ maxWidth: '70ch' }}>
                        {service.description}
                      </p>
                      <ul className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3">
                        {service.benefits.map((benefit) => (
                          <li key={benefit} className="flex items-start gap-2.5 text-sm text-[#aeb9c9]">
                            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#ffbf50' }} />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="fx-final" id="evaluacion">
          <div className="fx-container">
            <div>
              <span className="fx-eyebrow"><span className="fx-dot" /> ¿No sabés por dónde empezar?</span>
              <h2>Te ayudamos a elegir</h2>
              <p>Contanos tu situación y te recomendamos el servicio que mejor se adapta a tu empresa y tus objetivos de seguridad.</p>
            </div>
            <Link to="/solicitar-evaluacion" className="fx-button">
              Solicitar un servicio
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default ServicesPage;
